<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { useApi } from '../composables/api'

const route = useRoute()
const { notificationsApi } = useApi()

// A confirm step (rather than unsubscribing on page load) stops email link scanners from unsubscribing people.
const token = computed(() => {
  const value = route.query.token
  return typeof value === 'string' ? value : ''
})

type State = 'ready' | 'working' | 'done' | 'failed'
const state = ref<State>('ready')
const unsubscribedFrom = ref('')

async function confirm() {
  state.value = 'working'
  try {
    const response = await notificationsApi.notificationsUnsubscribeByToken({ token: token.value })
    unsubscribedFrom.value = response.displayName ?? 'these'
    state.value = 'done'
  } catch {
    state.value = 'failed'
  }
}
</script>

<template>
  <q-page padding class="layout-constrained">
    <div class="q-pa-md">
      <q-card>
        <q-card-section>
          <div class="text-h5 q-mb-md">Unsubscribe from emails</div>

          <div v-if="!token || state === 'failed'" data-testid="unsubscribe-invalid">
            <p class="text-body1">This unsubscribe link is invalid or has expired.</p>
            <p class="text-body1">
              You can change which emails you get from your account, or reply to any email from us
              and we'll sort it out.
            </p>
            <q-btn color="primary" label="Go to your account" :to="{ name: 'account' }" />
          </div>

          <div v-else-if="state === 'done'" data-testid="unsubscribe-done">
            <p class="text-body1">
              You've been unsubscribed from <strong>{{ unsubscribedFrom }}</strong> emails.
            </p>
            <p class="text-body1">
              Account and service emails (for example about your uploads or a takedown notice) will
              still be sent.
            </p>
            <q-btn color="primary" label="Continue to RoadFootage" :to="{ name: 'explore' }" />
          </div>

          <div v-else>
            <p class="text-body1">Confirm that you want to stop receiving these emails.</p>
            <q-btn
              color="primary"
              label="Unsubscribe"
              :loading="state === 'working'"
              data-testid="unsubscribe-confirm"
              @click="confirm"
            />
          </div>
        </q-card-section>
      </q-card>
    </div>
  </q-page>
</template>
